/**
 * KAN-757: одна таблица CRM вместо блока внимания и групп по дням.
 * Сверху строки "Требуют внимания" (сегодня), ниже отработанные клиенты
 * периода от свежих к старым. Период хранится в адресе (?from=&to=), по
 * умолчанию сегодня по Москве.
 * KAN-880: строка таблицы лидов (kind lead, GET /api/v1/crm/leads/): действия
 * по исходу и метке взятия в работу (rowActions).
 */
import type { AttentionAction } from '@/services/api/dashboard';
import type { CrmBoard, CrmLeadRow, CrmRow } from '@/types/crm';
import { moscowParts } from './crmBoard';
import { clampCrmRange } from './crmRange';
import { attentionRowActions, isTakenToWork } from './takenToWork';
import { isIdleRow, withWorkContinues } from './workContinues';

export type CrmTableRowKind = 'attention' | 'done' | 'lead';

export interface CrmTableRow {
  kind: CrmTableRowKind;
  row: CrmRow;
  /** Дата группы с сервера (ГГГГ-ММ-ДД), для отработанных без done_at. */
  day: string | null;
}

export interface CrmRange {
  /** ГГГГ-ММ-ДД, включительно. */
  from: string;
  to: string;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Сегодня по Москве: ГГГГ-ММ-ДД. */
export function todayMoscow(now: Date = new Date()): string {
  const p = moscowParts(now);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

function validDay(value: string | null | undefined): string | null {
  if (!value || !DAY_RE.test(value)) return null;
  return Number.isNaN(new Date(`${value}T00:00:00Z`).getTime()) ? null : value;
}

/**
 * Период из адреса: пустая или неверная граница = сегодня; начало позже конца: меняются местами;
 * длиннее 366 дней: начало подтягивается к концу (KAN-758).
 */
export function parseCrmRange(from: string | null, to: string | null, now: Date = new Date()): CrmRange {
  const today = todayMoscow(now);
  const a = validDay(from) ?? today;
  const b = validDay(to) ?? today;
  return clampCrmRange(a <= b ? { from: a, to: b } : { from: b, to: a });
}

const OUTCOME_ACTIONS: readonly AttentionAction[] = ['success', 'refused'];
const CLOSE_ONLY: readonly AttentionAction[] = ['done'];
const NO_ACTIONS: readonly AttentionAction[] = [];

/**
 * KAN-786, KAN-880, KAN-911: кнопки действий строки. "Отработано" (success /
 * refused) только у взятого в работу: до взятия только "Взять / Назначить в
 * работу" (done), иначе менеджер не назначается.
 * - lead: исход won/lost - ничего; не взят - done; взят - success, refused.
 * - attention: взята и исход не выставлен - success, refused (взятый клиент,
 *   снова требующий внимания); не взята - done (с исходом и без).
 * - done: исход выставлен - ничего; иначе success, refused.
 * Поле outcome отсутствует (старый backend): как open.
 * KAN-955: у строки с причиной "Нет движения" (row.idle) ещё "Работа идёт".
 */
export function rowActions(item: CrmTableRow): readonly AttentionAction[] {
  return withWorkContinues(baseRowActions(item), isIdleRow(item.row));
}

function baseRowActions(item: CrmTableRow): readonly AttentionAction[] {
  const decided = item.row.outcome === 'won' || item.row.outcome === 'lost';
  if (item.kind === 'lead') {
    if (decided) return NO_ACTIONS;
    return isTakenToWork(item.row) ? OUTCOME_ACTIONS : CLOSE_ONLY;
  }
  if (item.kind === 'attention') return attentionRowActions(item.row);
  return decided ? NO_ACTIONS : OUTCOME_ACTIONS;
}

/**
 * KAN-880: вид строки для разметки (data-kind): у лида таблицы attention
 * (требует внимания), taken (взят в работу) или lead; у старых строк их kind.
 */
export function tableRowKind(item: CrmTableRow): string {
  if (item.kind !== 'lead') return item.kind;
  const lead = item.row as Partial<CrmLeadRow>;
  if (lead.attention) return 'attention';
  return lead.taken_at ? 'taken' : 'lead';
}

function doneTime(item: CrmTableRow): number {
  const iso = item.row.done_at ?? (item.day ? `${item.day}T00:00:00+03:00` : null);
  const time = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/** Строки таблицы: внимание первым, затем отработанные от свежих к старым. */
export function crmTableRows(board: CrmBoard): CrmTableRow[] {
  const attention = board.attention.map((row): CrmTableRow => ({ kind: 'attention', row, day: null }));
  const done = board.days
    .flatMap((day) => day.rows.map((row): CrmTableRow => ({ kind: 'done', row, day: day.date })))
    .sort((x, y) => doneTime(y) - doneTime(x));
  return [...attention, ...done];
}
